import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { onboardingApi, ApiError } from "@/lib/api";
import { isFreeEmailDomain } from "@/lib/emailDomains";

// Step 1A — email + password. No other fields. The Zod schema rejects
// personal-email domains so the user gets an inline UX hint before the
// server round-trip. Backend maintains its own authoritative list.
const schema = z.object({
  email: z
    .string()
    .email("Enter a valid email")
    .refine((v) => !isFreeEmailDomain(v), {
      message: "Please use your business email",
    }),
  password: z.string().min(8, "At least 8 characters"),
});
type FormValues = z.infer<typeof schema>;

export function StepRegister() {
  const { signUp, deleteCurrentUser } = useAuth();
  const setOnboarding = useOnboardingStore((s) => s.setOnboarding);
  const setStep = useOnboardingStore((s) => s.setStep);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: FormValues) => {
    // 1. Firebase account.
    try {
      await signUp(values.email, values.password);
    } catch (err) {
      const message = (err as { message?: string }).message ?? "Something went wrong";
      console.error("signUp", err);
      toast.error("Sign-up failed", { description: message });
      return;
    }

    // 2. Backend onboarding row + verification email. The backend hard-requires
    //    email delivery in prod and either succeeds with verification_email_sent=true
    //    or fails 4xx/5xx (already rolling back its own row). On any failure we
    //    must roll back the Firebase user too — otherwise the email is "burned"
    //    against an account the user can't ever finish creating.
    //
    //    No "continue with Firebase only" branch any more: we will not advance to
    //    the "Check your inbox" page unless the backend confirms the email was
    //    actually delivered. Showing false success is the bug we're closing.
    let res;
    try {
      res = await onboardingApi.register(values.email, values.password);
    } catch (err) {
      const ae = err as ApiError;
      console.error("onboarding.register", ae);
      try {
        await deleteCurrentUser();
      } catch (rollbackErr) {
        console.error("rollback firebase user", rollbackErr);
      }
      const description =
        ae.code === "email_provider_unavailable"
          ? "We can't send verification emails right now. Please try again in a few minutes or contact support."
          : ae.code === "email_send_failed"
          ? "We couldn't deliver your verification email. Please try again in a moment."
          : ae.status === 0
          ? "We couldn't reach VirtualDesk. Check your connection and try again."
          : ae.message || "Please try again.";
      toast.error("Registration failed", { description });
      return;
    }

    if (!res.verification_email_sent) {
      // Backend returned 201 but couldn't actually send the email (only happens
      // in non-prod with the noop sender). We do NOT lie to the user about a
      // "Check your inbox" page that points to nothing — surface the dev URL
      // when one is provided, otherwise fail loud.
      if (res.dev_verify_url) {
        setOnboarding(res.onboarding_id, values.email);
        setStep("verify-email");
        toast.message("Dev mode — email not actually sent", {
          description: `Open the verify link from the server logs (provider: ${res.verification_email_provider}).`,
        });
        return;
      }
      try {
        await deleteCurrentUser();
      } catch (rollbackErr) {
        console.error("rollback firebase user", rollbackErr);
      }
      toast.error("Registration failed", {
        description: "We couldn't send your verification email. Please try again.",
      });
      return;
    }

    setOnboarding(res.onboarding_id, values.email);
    setStep("verify-email");
    toast.success("Verification email sent", {
      description: `We sent a link to ${values.email}. Check your inbox.`,
    });
  };

  const emailError = form.formState.errors.email?.message;
  const passwordError = form.formState.errors.password?.message;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
            <UserPlus className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Create your account</CardTitle>
            <CardDescription>
              Start your VirtualDesk workspace in under ten minutes.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="email">
              Work email
              <span aria-hidden="true" className="ml-1 text-signal-failed">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={!!emailError}
              aria-describedby={emailError ? "email-error" : undefined}
              {...form.register("email")}
            />
            {emailError && (
              <p id="email-error" className="text-xs text-signal-failed">
                {emailError}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">
              Password
              <span aria-hidden="true" className="ml-1 text-signal-failed">*</span>
            </Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              aria-invalid={!!passwordError}
              aria-describedby={passwordError ? "password-error" : undefined}
              {...form.register("password")}
            />
            {passwordError && (
              <p id="password-error" className="text-xs text-signal-failed">
                {passwordError}
              </p>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create account
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
